import React, { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import cx from 'classnames';
import { cloneDeep, isEqual } from 'lodash/lang';

import useAppInfo from '../../../../../../../../common/hooks/useAppInfo';
import { CustomFieldLabelClosableBlock } from '../CustomFieldsGenerator/CustomFieldLabelClosableBlock';
import { getCombinedList } from '../ItemGroupSetupService';
import { OptionalField } from '../OptionalFields/OptionalField';

import { FieldsHeader } from './FieldsHeader';
import {
  FINANCE_COLUMN,
  FIXED_FIELD_COLUMN,
  INVESTIGATOR_FIELD_COLUMN,
  ITEM_SEQUENCE_COLUMN,
  LOG_VIEW_COLUMN,
  UPDATED_CDASH_ITEMS_COLUMN,
  useItemGroupSetupGridConfig
} from './gridService';

import './FieldsToSelect.scss';

export function FieldsToSelect({
  inputs,
  hideIsTableOptions,
  updateInputsAndPreviewData,
  getCheckboxForTableView,
  getInvestigatorFieldCheckboxForTableView,
  getExpenseFixedCheckboxForTableView,
  onChangeLabelTextHandler,
  previewDataList,
  editMode,
  updateState,
  domainId,
  domainConditionalLogic,
  changeSequenceCounter,
  domainArray,
  sequenceCounter,
  isElligoPatientStatusDomain,
  setCombinedLabelList,
  terminologyVersionGroupNumber,
  isLogType,
  domain,
  onFinanceFieldChange
}) {
  const [currentInputs, setCurrentInputs] = useState(null);
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [combinedInputs, setCombinedInputs] = useState([]);

  const { columnsToShow, layout } = useItemGroupSetupGridConfig(domain, isLogType, hideIsTableOptions);
  const appInfo = useAppInfo();
  const itemGroupFinanceEventEnabled = appInfo?.features?.itemGroupFinanceEventEnabled;

  const prepareCombinedInputs = useCallback(() => {
    const combinedList = getCombinedList(inputs, selectedOnly);
    if (!isEqual(combinedList, combinedInputs)) {
      setCombinedInputs(combinedList);
      changeSequenceCounter(combinedList.length + 1);
    }
    setCombinedLabelList(combinedList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs]);

  useEffect(() => {
    if (!isEqual(currentInputs, inputs)) {
      setCurrentInputs(cloneDeep(inputs));
      prepareCombinedInputs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs]);

  useEffect(() => {
    setSelectedOnly(false);
  }, [domainId]);

  useEffect(() => {
    const combinedList = getCombinedList(inputs, selectedOnly);
    setCombinedInputs(combinedList);
    changeSequenceCounter(combinedList.length + 1);
    updateInputsAndPreviewData(inputs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOnly]);

  function handleOnDragEnd(result) {
    const items = Array.from(combinedInputs);
    const [reorderedItem] = items.splice(result.source.index, 1);
    if (result?.destination && result.destination.index >= 0) {
      items.splice(result.destination.index, 0, reorderedItem);

      let addIndex = 0;
      items.forEach((item, index) => {
        if (item.isOptional) {
          item.id = addIndex;
          item.sequence = addIndex + 1;
          addIndex = addIndex + 1;
        }
        if (item.isCustom) {
          item.id = addIndex;
          item.sequence = addIndex + 1;
          item.customItemDefinationList.forEach((customItem, idx) => {
            customItem.sequence = addIndex + 1;
            addIndex = addIndex + 1;
          });
        }
      });
      setCombinedInputs(items);
      updateInputsAndPreviewData(inputs);
    }
  }

  return (
    <div className="fields-to-select">
      <FieldsHeader
        terminologyVersionGroupNumber={terminologyVersionGroupNumber}
        inputs={inputs}
        updateInputsAndPreviewData={updateInputsAndPreviewData}
        updateState={updateState}
        domainArray={domainArray}
        changeSequenceCounter={changeSequenceCounter}
        sequenceCounter={sequenceCounter}
        onSelectedOnly={setSelectedOnly}
        selectedOnly={selectedOnly}
        isElligoPatientStatusDomain={isElligoPatientStatusDomain}
      />
      <div className="item-group-setup-grid" data-layout={layout}>
        <div className="igs-grid-header">
          <div className="igs-grid-header-column" data-column="CDASH_ITEMS">
            CDASH Items
          </div>
          {columnsToShow.includes(FINANCE_COLUMN) && (
            <div className="igs-grid-header-column" data-column="FINANCE">
              {itemGroupFinanceEventEnabled && 'Finance'}
            </div>
          )}
          <div className="igs-grid-header-column" data-column="CDASH">
            CDASH
          </div>
          {columnsToShow.includes(UPDATED_CDASH_ITEMS_COLUMN) && (
            <div className="igs-grid-header-column" data-column="UPDATED_CDASH_ITEMS">
              Updated CDASH Items
            </div>
          )}
          <div className="igs-grid-header-column" data-column="ANSWER_TYPE">
            Answer Type
          </div>
          {columnsToShow.includes(ITEM_SEQUENCE_COLUMN) && (
            <div className="igs-grid-header-column" data-column="ITEM_SEQUENCE">
              Item Sequence
            </div>
          )}
          {columnsToShow.includes(FIXED_FIELD_COLUMN) && (
            <div className="igs-grid-header-column" data-column="FIXED_FIELD">
              Fixed Field
            </div>
          )}
          {columnsToShow.includes(INVESTIGATOR_FIELD_COLUMN) && (
            <div className="igs-grid-header-column" data-column="INVESTIGATOR_FIELD">
              Investigator Field
            </div>
          )}
          {columnsToShow.includes(LOG_VIEW_COLUMN) && (
            <div className="igs-grid-header-column" data-column="LOG_VIEW">
              Log View
            </div>
          )}
        </div>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="items">
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="igs-grid-body">
                {inputs.length > 0 &&
                  combinedInputs.map((data, labelIndex) => {
                    return (
                      <React.Fragment key={labelIndex}>
                        {data.mandatoryField && (
                          <div className="igs-grid-body-row">
                            <OptionalField
                              terminologyVersionGroupNumber={terminologyVersionGroupNumber}
                              key={data.itemIndex}
                              getCheckboxForTableView={getCheckboxForTableView}
                              getInvestigatorFieldCheckboxForTableView={getInvestigatorFieldCheckboxForTableView}
                              getExpenseFixedCheckboxForTableView={getExpenseFixedCheckboxForTableView}
                              onChangeLabelTextHandler={onChangeLabelTextHandler}
                              previewDataList={previewDataList}
                              editMode={editMode}
                              item={data}
                              index={data.itemIndex}
                              formLabelData={data}
                              domain={domain}
                              labelIndex={data.labelIndex}
                              inputs={inputs}
                              updateInputsAndPreviewData={updateInputsAndPreviewData}
                              updateState={updateState}
                              columnsToShow={columnsToShow}
                              onFinanceFieldChange={onFinanceFieldChange}
                            />
                          </div>
                        )}
                        {data.isOptional && !data.mandatoryField && (
                          <Draggable key={data.name} draggableId={data.name} index={labelIndex}>
                            {(provided, snapshot) => (
                              <div
                                className={cx('igs-grid-body-row', {
                                  'igs-grid-body-row-dragging': snapshot.isDragging
                                })}
                                data-layout={layout}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <OptionalField
                                  terminologyVersionGroupNumber={terminologyVersionGroupNumber}
                                  key={data.itemIndex}
                                  getCheckboxForTableView={getCheckboxForTableView}
                                  getInvestigatorFieldCheckboxForTableView={getInvestigatorFieldCheckboxForTableView}
                                  getExpenseFixedCheckboxForTableView={getExpenseFixedCheckboxForTableView}
                                  onChangeLabelTextHandler={onChangeLabelTextHandler}
                                  previewDataList={previewDataList}
                                  editMode={editMode}
                                  item={data}
                                  index={data.itemIndex}
                                  formLabelData={data}
                                  domain={domain}
                                  domainConditionalLogic={domainConditionalLogic}
                                  labelIndex={data.labelIndex}
                                  inputs={inputs}
                                  updateInputsAndPreviewData={updateInputsAndPreviewData}
                                  updateState={updateState}
                                  columnsToShow={columnsToShow}
                                  onFinanceFieldChange={onFinanceFieldChange}
                                />
                              </div>
                            )}
                          </Draggable>
                        )}
                        {data.isCustom && !data.mandatoryField && (
                          <Draggable key={data.name} draggableId={labelIndex.toString()} index={labelIndex}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                className={cx('igs-grid-body-row igs-grid-body-row-custom-fields', {
                                  'igs-grid-body-row-dragging': snapshot.isDragging
                                })}
                                data-layout={layout}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <CustomFieldLabelClosableBlock
                                  terminologyVersionGroupNumber={terminologyVersionGroupNumber}
                                  editMode={editMode}
                                  key={data.labelIndex}
                                  getCheckboxForTableView={getCheckboxForTableView}
                                  getInvestigatorFieldCheckboxForTableView={getInvestigatorFieldCheckboxForTableView}
                                  getExpenseFixedICheckboxForTableView={getExpenseFixedCheckboxForTableView}
                                  customFieldData={data}
                                  domain={domain}
                                  labelIndex={data.labelIndex}
                                  inputs={inputs}
                                  updateInputsAndPreviewData={updateInputsAndPreviewData}
                                  updateState={updateState}
                                  onChangeLabelTextHandler={onChangeLabelTextHandler}
                                  columnsToShow={columnsToShow}
                                  onFinanceFieldChange={onFinanceFieldChange}
                                />
                              </div>
                            )}
                          </Draggable>
                        )}
                      </React.Fragment>
                    );
                  })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
}
